import React from 'react';
import {
	Popover,
	PopoverTrigger,
	PopoverContent,
	PopoverBody,
	Button,
	Flex,
	VStack,
	Text,
	IconButton,
	Input,
	InputGroup,
	InputLeftElement,
	Checkbox,
	Box,
} from '@chakra-ui/react';
import { ChevronDownIcon, CloseIcon, SearchIcon } from '@chakra-ui/icons';

import { useUnifiedFilterLogic } from './useUnifiedFilterLogic';

interface IUnifiedFilterPillProps {
	filterKey: string;
	label: string;
	options: string[];
	selectedOptions: string[];
	isMulti?: boolean;
	showColors?: boolean;
	disabled?: boolean;
	onApplyFilters: () => void;
}

export const UnifiedFilterPill: React.FC<IUnifiedFilterPillProps> = ({
	filterKey,
	label,
	options,
	selectedOptions,
	isMulti = true,
	showColors = false,
	disabled = false,
	onApplyFilters,
}) => {
	const {
		ref,
		isOpen,
		onOpen,
		onClose,
		filteredOptions,
		initialFocusRef,
		search,
		handleSearchChange,
		handleClear,
		handleOptionClick,
		filterButtonHoverStyle,
		filterOptionHoverStyle,
		filterPillResetHover,
		filterPopoverOutline,
		colorMode,
		contrastColor,
		settings,
		calendarBordersColor,
	} = useUnifiedFilterLogic({ filterKey, options, isMulti, onApplyFilters });

	return (
		<Popover
			isOpen={isOpen}
			onOpen={onOpen}
			onClose={onClose}
			initialFocusRef={initialFocusRef}
			placement='bottom-start'
		>
			<PopoverTrigger>
				<Box position='relative' display='inline-block'>
					<Button
						variant='outline'
						rightIcon={<ChevronDownIcon color={contrastColor} />}
						borderRadius='full'
						borderColor={calendarBordersColor}
						isDisabled={disabled}
						_hover={filterButtonHoverStyle}
					>
						{label}
					</Button>
					{selectedOptions.length > 0 && (
						<Box
							position='absolute'
							top='-6px'
							right='-6px'
							bg='red.500'
							color='white'
							borderRadius='full'
							boxSize='18px'
							display='flex'
							alignItems='center'
							justifyContent='center'
							cursor='pointer'
							zIndex={2}
							onClick={handleClear}
							_hover={filterPillResetHover}
						>
							<CloseIcon boxSize={2.5} />
						</Box>
					)}
				</Box>
			</PopoverTrigger>
			<PopoverContent borderRadius='md' shadow='md' minW='250px' p={3} _focus={filterPopoverOutline}>
				<PopoverBody p={0} ref={ref}>
					<Flex flexDir='row' justify='space-between' alignItems={'flex-start'} mb={2} px={1} maxH='30px'>
						<Text fontWeight='semibold' fontSize='sm'>
							{label}
						</Text>
						<IconButton
							size='sm'
							aria-label='Close'
							icon={<CloseIcon boxSize={2.5} />}
							variant='ghost'
							onClick={onClose}
							boxShadow='none'
						/>
					</Flex>

					{isMulti && (
						<InputGroup mb={3}>
							<InputLeftElement pointerEvents='none' top={'-4px'}>
								<SearchIcon color='gray.400' />
							</InputLeftElement>
							<Input
								ref={initialFocusRef}
								placeholder='Search'
								size='sm'
								value={search}
								onChange={handleSearchChange}
							/>
						</InputGroup>
					)}

					<VStack align='start' spacing={2} maxH='200px' overflowY='auto' px={1}>
						{filteredOptions.map((option) => {
							const isSelected = selectedOptions.includes(option);
							return (
								<Flex
									key={option}
									px={2}
									py={1}
									align='center'
									justify='space-between'
									cursor='pointer'
									width='full'
									_hover={filterOptionHoverStyle}
									onClick={handleOptionClick(option)}
									borderRadius='md'
									bg={
										!isMulti && selectedOptions[0] === option
											? colorMode === 'light'
												? 'blue.200'
												: 'blue.300'
											: ''
									}
								>
									<Flex align='center'>
										{showColors && settings.eventCategoryColors[option]?.background && (
											<Box
												bg={settings.eventCategoryColors[option].background}
												borderRadius='full'
												width='10px'
												height='10px'
												mr={2}
											/>
										)}
										<Text fontSize='sm' margin='0px !important'>
											{option}
										</Text>
									</Flex>
									{isMulti && (
										<Checkbox
											isChecked={isSelected}
											pointerEvents='none'
											size='sm'
											colorScheme='blue'
											border={`1px solid ${contrastColor} !important`}
										/>
									)}
								</Flex>
							);
						})}
					</VStack>
				</PopoverBody>
			</PopoverContent>
		</Popover>
	);
};
